import path from "node:path";
import type { DependencyNode } from "@skaile/workspaces/asset-manager";
import { AssetManager } from "@skaile/workspaces/asset-manager";
import { Command } from "commander";
import { bareSoftDepName, colorRef, logInfo, S } from "../helpers.ts";

function printTree(node: DependencyNode, prefix = "", isLast = true): void {
  const connector = prefix === "" ? "" : isLast ? "└─ " : "├─ ";
  const line = prefix + connector + colorRef(node.ref);
  console.log(`  ${line}`);
  const childPrefix = prefix + (prefix === "" ? "" : isLast ? "   " : "│  ");
  for (let i = 0; i < node.children.length; i++) {
    printTree(node.children[i], childPrefix, i === node.children.length - 1);
  }
}

/**
 * Creates the `skaile tree` command.
 *
 * Prints the full transitive dependency tree of the installed assets using
 * ASCII art (├─ / └─), with each node coloured by asset kind.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/misc#tree
 */
export function makeTreeCommand(): Command {
  return new Command("tree")
    .description("Show full dependency tree")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (opts) => {
      const am = new AssetManager({ projectDir: path.resolve(opts.projectDir) });
      const root = am.tree();
      if (root.children.length === 0) {
        logInfo("No dependencies in lock file. Run `skaile install` first.");
        return;
      }
      console.log();
      printTree(root);
      // The v2 lock records no edges, so soft body-link deps aren't woven into the
      // tree above — list them distinctly below (report-only, inferred).
      const softDeps = await am.softDepsOf(root.children.map((c) => c.ref));
      if (softDeps.length > 0) {
        console.log(`  ${S.label("Soft dependencies (inferred)")}:`);
        for (const e of softDeps)
          console.log(
            `    ${S.dim("·")}  ${bareSoftDepName(e.from)} → ${bareSoftDepName(e.to)} ${S.dim("(soft)")}`,
          );
      }
      console.log();
    });
}
